import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  type SubscriptionStatus,
  holdsPlan,
} from '@structural-climbing/ui'

import { useGymThemeContext } from '../layouts/useGymTheme'
import { MemberSearchInput } from '../features/members/MemberSearchInput'
import { AddMemberModal } from '../features/members/AddMemberModal'
import { AssignPlanModal, type RenewTarget } from '../features/members/AssignPlanModal'
import { renewTargetFor } from '../features/members/renewTarget'
import { EditPlanModal } from '../features/members/EditPlanModal'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { ChildProfileModal } from '../features/members/ChildProfileModal'
import { MemberAvatar } from '../features/members/MemberAvatar'
import { MemberName } from '../features/members/MemberName'
import { ClubMemberStar } from '../features/members/ClubMemberStar'
import { MemberRow, ChildrenToggle } from '../features/members/MemberRow'
import { useGymTimezone } from '../contexts/GymContext'
import { CheckInActions } from '../features/members/CheckInActions'
import { AssignTrainerModal } from '../features/trainers/AssignTrainerModal'
import {
  usePaginatedMembers,
  MEMBERS_PAGE_SIZE,
  type StatusFilter,
  type PaginatedMember,
} from '../features/members/usePaginatedMembers'
import type { Subscription, UpcomingSubscription } from '../features/plans/types'
import { memberDisplayName } from '../features/members/memberDisplayName'
import { useCheckIn } from '../features/checkIn/useCheckIn'
import { useCheckOut } from '../features/checkIn/useCheckOut'
import { useTabParam } from '@structural-climbing/ui'
import { PageHeader } from '../components/PageHeader'
import { ListPages } from '../components/ListPages'
import { usePlanLine } from '../features/members/usePlanLine'

const STATUS_FILTERS: readonly StatusFilter[] = [
  'all',
  'active',
  'expired',
  'none',
  'club_member',
  'desk',
]

export function MembersPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const planLine = usePlanLine()
  const timezone = useGymTimezone()

  /** "next plan from 11.11.2026" once a renewal is queued, else the open renewal discount. */
  function renewalDetail(offer: { percent: number } | null, upcomingStartsAt: string | null) {
    if (upcomingStartsAt) {
      return t('members.renewedNext', {
        date: new Date(upcomingStartsAt).toLocaleDateString(i18n.language, {
          timeZone: timezone ?? undefined,
        }),
      })
    }
    if (offer) {
      return (
        <span className="text-success">
          {t('members.renewalOfferChip', { percent: offer.percent })}
        </span>
      )
    }
    return null
  }
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { primaryColor } = useGymThemeContext()

  const [search, setSearch] = useState('')
  // In the URL so the dashboard's "Active members" card can open it filtered.
  const [statusFilter, setStatusFilter] = useTabParam('status', STATUS_FILTERS, 'all')
  const [memberCounts, setMemberCounts] = useState<{
    all: number
    active: number
    expired: number
    none: number
    club_member: number
    desk: number
  } | null>(null)

  const {
    members,
    total,
    page,
    goToPage,
    loading,
    reload,
    plans,
    checkedInMap,
    childCheckedInMap,
  } = usePaginatedMembers(search, statusFilter)
  const hasAddonPlans = plans.some((p) => p.is_active && p.is_addon)

  const { checkIn, stateFor } = useCheckIn()
  const { checkOut, isCheckingOut, isAlreadyCheckedOut } = useCheckOut(reload)

  /** After a change that can move someone between filters: the list and the counts. */
  function refresh() {
    void reload()
    void loadCounts()
  }

  async function loadCounts() {
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/members/counts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setMemberCounts((await res.json()) as typeof memberCounts)
    } catch {
      // non-critical — tabs still work without counts
    }
  }

  useEffect(() => {
    void loadCounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [adding, setAdding] = useState(false)

  // Children stay folded under their parent until the desk opens them.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const [assignTarget, setAssignTarget] = useState<{
    memberEmail: string
    memberUserId: string | null
    memberName?: string
    childId?: string
    childName?: string
    addonsOnly?: boolean
    renewOf?: RenewTarget
  } | null>(null)

  const [editTarget, setEditTarget] = useState<{
    subscription: Subscription
    memberEmail: string
    /** The renewal queued behind it, which an edit can move. */
    upcoming?: UpcomingSubscription | null
  } | null>(null)

  const [profileTarget, setProfileTarget] = useState<string | null>(null)
  const [childProfileTarget, setChildProfileTarget] = useState<string | null>(null)
  const [assignTrainerTarget, setAssignTrainerTarget] = useState<{
    userId: string
    email: string
    name: string
  } | null>(null)

  const [deleteMemberTarget, setDeleteMemberTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  async function handleDeleteMember() {
    if (!deleteMemberTarget) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/join-requests/${deleteMemberTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setDeleteError(data.error ?? t('members.deleteError'))
        return
      }
      setDeleteMemberTarget(null)
      refresh()
    } finally {
      setIsDeleting(false)
    }
  }

  async function toggleClubMember(member: PaginatedMember) {
    if (!member.user_id) return
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/member-flags/${encodeURIComponent(member.user_id)}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_club_member: !member.flags.is_club_member }),
    })
    refresh()
  }

  const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
    all: t('members.filterAll'),
    active: t('members.filterActive'),
    expired: t('members.filterExpired'),
    none: t('members.filterNone'),
    club_member: t('members.filterClubMember'),
    desk: t('members.filterDesk'),
  }

  function toggleExpanded(memberId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(memberId)) next.delete(memberId)
      else next.add(memberId)
      return next
    })
  }

  function renderCheckInActions(
    member: PaginatedMember,
    subStatus: SubscriptionStatus | undefined,
    inactiveAction: ReactNode,
  ) {
    if (!member.user_id) return null
    return (
      <CheckInActions
        userId={member.user_id}
        subStatus={subStatus}
        checkInState={stateFor(member.user_id)}
        activeEntry={checkedInMap.get(member.user_id)}
        isCheckingOut={isCheckingOut}
        isAlreadyCheckedOut={isAlreadyCheckedOut}
        onCheckIn={(userId) => void checkIn(userId)}
        onCheckOut={(id, isGuest) => void checkOut(id, isGuest)}
        inactiveAction={inactiveAction}
      />
    )
  }

  function renderChildCheckInActions(
    userId: string,
    childId: string,
    childSub: SubscriptionStatus | undefined,
    inactiveAction: ReactNode,
  ) {
    return (
      <CheckInActions
        userId={userId}
        childId={childId}
        subStatus={childSub}
        checkInState={stateFor(userId, childId)}
        activeEntry={childCheckedInMap.get(childId)}
        isCheckingOut={isCheckingOut}
        isAlreadyCheckedOut={isAlreadyCheckedOut}
        onCheckIn={(uid, cid) => void checkIn(uid, cid)}
        onCheckOut={(id) => void checkOut(id, false)}
        inactiveAction={inactiveAction}
      />
    )
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('members.title')}
        subtitle={t('members.subtitle')}
        primaryAction={{ label: t('members.addMember'), onPress: () => setAdding(true) }}
      />

      <Card>
        <CardHeader>
          <div className="flex w-full flex-col gap-3">
            <MemberSearchInput value={search} onValueChange={setSearch} className="w-full" />
            <div className="-mx-1 flex gap-1 overflow-x-auto px-1 [scrollbar-width:none] md:flex-wrap">
              {STATUS_FILTERS.map((f) => {
                const isActive = statusFilter === f
                return (
                  <Button
                    key={f}
                    size="sm"
                    radius="full"
                    variant={isActive ? 'solid' : 'flat'}
                    color={isActive ? 'primary' : 'default'}
                    className="shrink-0 font-semibold"
                    onClick={() => setStatusFilter(f)}
                  >
                    {STATUS_FILTER_LABELS[f]}
                    {memberCounts !== null && ` (${memberCounts[f] ?? 0})`}
                  </Button>
                )
              })}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {loading && members.length === 0 ? (
            <div className="flex justify-center py-8">
              <Spinner size="sm" color="primary" />
            </div>
          ) : members.length === 0 ? (
            <p className="py-8 text-center text-sm text-foreground/40">
              {t('members.noMembersFound')}
            </p>
          ) : (
            <div
              className={`flex flex-col divide-y divide-divider transition-opacity ${loading ? 'opacity-60' : ''}`}
            >
              {members.map((member) => {
                const activeSub = member.subscription
                const subStatus = activeSub?.effective_status
                const renewOf = renewTargetFor(activeSub, !!member.upcoming_subscription)
                const children = member.children ?? []
                // A search that matched a child shows that child straight away.
                const isExpanded = children.length > 0 && (expandedIds.has(member.id) || !!search)
                const name = memberDisplayName(member)

                return (
                  <div key={member.id}>
                    <MemberRow
                      avatar={
                        <MemberAvatar
                          userId={member.user_id}
                          name={name}
                          avatarExt={member.avatar_ext}
                          avatarUpdatedAt={member.avatar_updated_at}
                          primaryColor={primaryColor}
                          size="md"
                        />
                      }
                      name={
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <MemberName
                            name={name}
                            nickname={member.nickname}
                            className="text-sm font-semibold"
                          />
                          {member.flags.is_club_member && <ClubMemberStar />}
                          {member.is_guest && <SmallTag>{t('members.guestBadge')}</SmallTag>}
                          {member.added_at_desk && <SmallTag>{t('members.deskBadge')}</SmallTag>}
                          {!member.user_id && !member.is_guest && (
                            <SmallTag>{t('members.noAccountBadge')}</SmallTag>
                          )}
                        </span>
                      }
                      onOpen={member.user_id ? () => setProfileTarget(member.user_id) : undefined}
                      openLabel={t('members.viewProfile')}
                      status={subStatus}
                      details={[
                        planLine(activeSub) ?? (member.user_email || member.contact || null),
                        renewalDetail(
                          activeSub?.renewal_offer ?? null,
                          member.upcoming_subscription?.starts_at ?? null,
                        ),
                        children.length > 0 ? (
                          <ChildrenToggle
                            count={children.length}
                            isOpen={isExpanded}
                            onToggle={() => toggleExpanded(member.id)}
                          />
                        ) : null,
                      ]}
                      actions={
                        <>
                          {member.user_id &&
                            renderCheckInActions(
                              member,
                              subStatus,
                              holdsPlan(subStatus) ? null : (
                                <Button
                                  size="sm"
                                  variant="bordered"
                                  className="border-1"
                                  onPress={() =>
                                    setAssignTarget({
                                      memberEmail: member.user_email,
                                      memberUserId: member.user_id,
                                      memberName: name,
                                    })
                                  }
                                >
                                  {subStatus ? t('members.renewPlan') : t('members.sellPlan')}
                                </Button>
                              ),
                            )}
                          <Dropdown>
                            <DropdownTrigger>
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                title={t('members.moreActions')}
                                aria-label={t('members.moreActions')}
                              >
                                <MoreIcon />
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                              aria-label={t('members.moreActions')}
                              onAction={(key) => {
                                if (key === 'plan') {
                                  if (holdsPlan(subStatus)) {
                                    setEditTarget({
                                      subscription: activeSub!,
                                      memberEmail: member.user_email,
                                      upcoming: member.upcoming_subscription ?? null,
                                    })
                                  } else {
                                    setAssignTarget({
                                      memberEmail: member.user_email,
                                      memberUserId: member.user_id,
                                      memberName: name,
                                    })
                                  }
                                } else if (key === 'renew' && renewOf) {
                                  setAssignTarget({
                                    memberEmail: member.user_email,
                                    memberUserId: member.user_id,
                                    memberName: name,
                                    renewOf,
                                  })
                                } else if (key === 'add-addon') {
                                  setAssignTarget({
                                    memberEmail: member.user_email,
                                    memberUserId: member.user_id,
                                    memberName: name,
                                    addonsOnly: true,
                                  })
                                } else if (key === 'assign-trainer') {
                                  setAssignTrainerTarget({
                                    userId: member.user_id!,
                                    email: member.user_email,
                                    name,
                                  })
                                } else if (key === 'club-member') {
                                  void toggleClubMember(member)
                                } else if (key === 'delete') {
                                  setDeleteMemberTarget({ id: member.id, name })
                                }
                              }}
                            >
                              {member.user_id ? (
                                <DropdownItem key="plan">
                                  {holdsPlan(subStatus)
                                    ? t('members.editPlan')
                                    : t('members.assignPlan')}
                                </DropdownItem>
                              ) : null}
                              {member.user_id && renewOf ? (
                                <DropdownItem key="renew">{t('members.renewPlan')}</DropdownItem>
                              ) : null}
                              {member.user_id && hasAddonPlans ? (
                                <DropdownItem key="add-addon">{t('members.addAddon')}</DropdownItem>
                              ) : null}
                              {member.user_id ? (
                                <DropdownItem key="assign-trainer">
                                  {t('members.assignTrainer')}
                                </DropdownItem>
                              ) : null}
                              {member.user_id ? (
                                <DropdownItem key="club-member">
                                  {member.flags.is_club_member
                                    ? t('members.removeClubMember')
                                    : t('members.markClubMember')}
                                </DropdownItem>
                              ) : null}
                              <DropdownItem key="delete" className="text-danger" color="danger">
                                {t('members.deleteButton')}
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </>
                      }
                    />

                    {isExpanded && (
                      <div className="mb-2 ml-3 border-l-2 border-divider pl-3 md:ml-12">
                        {children.map((child) => {
                          const childSubStatus = child.subscription_status ?? undefined
                          const childRenewOf = renewTargetFor(
                            child.subscription,
                            !!child.upcoming_subscription,
                          )
                          const childFullName = [child.first_name, child.last_name]
                            .filter(Boolean)
                            .join(' ')
                          const assignChild = (renew?: RenewTarget) =>
                            setAssignTarget({
                              memberEmail: member.user_email,
                              memberUserId: member.user_id,
                              childId: child.id,
                              childName: childFullName,
                              renewOf: renew,
                            })
                          return (
                            <MemberRow
                              key={child.id}
                              avatar={
                                <MemberAvatar
                                  userId={null}
                                  name={childFullName}
                                  avatarExt={null}
                                  avatarUpdatedAt={null}
                                  primaryColor={primaryColor}
                                />
                              }
                              name={
                                <span className="flex flex-wrap items-center gap-x-1.5">
                                  <span className="text-sm font-medium">{childFullName}</span>
                                  <SmallTag>{t('members.childBadge')}</SmallTag>
                                </span>
                              }
                              onOpen={() => setChildProfileTarget(child.id)}
                              openLabel={t('members.viewProfile')}
                              status={childSubStatus}
                              details={[
                                planLine(child.subscription),
                                renewalDetail(
                                  child.subscription?.renewal_offer ?? null,
                                  child.upcoming_subscription?.starts_at ?? null,
                                ),
                              ]}
                              actions={
                                <>
                                  {renderChildCheckInActions(
                                    member.user_id!,
                                    child.id,
                                    childSubStatus,
                                    holdsPlan(childSubStatus) ? null : (
                                      <Button
                                        size="sm"
                                        variant="bordered"
                                        className="border-1"
                                        onPress={() => assignChild()}
                                      >
                                        {childSubStatus
                                          ? t('members.renewPlan')
                                          : t('members.sellPlan')}
                                      </Button>
                                    ),
                                  )}
                                  <Dropdown>
                                    <DropdownTrigger>
                                      <Button
                                        isIconOnly
                                        size="sm"
                                        variant="light"
                                        title={t('members.moreActions')}
                                        aria-label={t('members.moreActions')}
                                      >
                                        <MoreIcon />
                                      </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu
                                      aria-label={t('members.moreActions')}
                                      onAction={(key) => {
                                        if (key === 'plan') {
                                          const sub = child.subscription
                                          if (holdsPlan(childSubStatus) && sub) {
                                            setEditTarget({
                                              subscription: sub,
                                              memberEmail: member.user_email,
                                              upcoming: child.upcoming_subscription ?? null,
                                            })
                                          } else {
                                            assignChild()
                                          }
                                        } else if (key === 'renew' && childRenewOf) {
                                          assignChild(childRenewOf)
                                        }
                                      }}
                                    >
                                      <DropdownItem key="plan">
                                        {holdsPlan(childSubStatus)
                                          ? t('members.editPlan')
                                          : t('members.assignPlan')}
                                      </DropdownItem>
                                      {childRenewOf ? (
                                        <DropdownItem key="renew">
                                          {t('members.renewPlan')}
                                        </DropdownItem>
                                      ) : null}
                                    </DropdownMenu>
                                  </Dropdown>
                                </>
                              }
                            />
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <ListPages
        page={page}
        pageSize={MEMBERS_PAGE_SIZE}
        total={total}
        onPageChange={goToPage}
        isLoading={loading}
      />

      {assignTarget && (
        <AssignPlanModal
          memberEmail={assignTarget.memberEmail}
          memberUserId={assignTarget.memberUserId}
          memberName={assignTarget.memberName}
          childId={assignTarget.childId}
          childName={assignTarget.childName}
          plans={plans}
          addonsOnly={assignTarget.addonsOnly}
          renewOf={assignTarget.renewOf}
          onAssigned={() => {
            setAssignTarget(null)
            refresh()
          }}
          onClose={() => setAssignTarget(null)}
        />
      )}

      {adding && (
        <AddMemberModal
          onClose={() => setAdding(false)}
          onAdded={(added) => {
            setAdding(false)
            void reload()
            void loadCounts()
            // A plan is the next thing the desk gives them.
            setAssignTarget({
              memberEmail: added.email,
              memberUserId: added.userId,
              memberName: added.name,
            })
          }}
        />
      )}

      {editTarget && (
        <EditPlanModal
          subscription={editTarget.subscription}
          memberEmail={editTarget.memberEmail}
          upcoming={editTarget.upcoming}
          plans={plans}
          onUpdated={() => {
            setEditTarget(null)
            refresh()
          }}
          onDeleted={() => {
            setEditTarget(null)
            refresh()
          }}
          onClose={() => setEditTarget(null)}
        />
      )}

      {profileTarget && (
        <MemberProfileModal
          userId={profileTarget}
          onClose={() => setProfileTarget(null)}
          onChanged={() => {
            void reload()
            void loadCounts()
          }}
        />
      )}

      {childProfileTarget && (
        <ChildProfileModal
          childId={childProfileTarget}
          onClose={() => setChildProfileTarget(null)}
          onChanged={() => {
            void reload()
            void loadCounts()
          }}
          onOpenParent={(userId) => {
            setChildProfileTarget(null)
            setProfileTarget(userId)
          }}
        />
      )}

      {assignTrainerTarget && (
        <AssignTrainerModal
          memberUserId={assignTrainerTarget.userId}
          memberEmail={assignTrainerTarget.email}
          memberName={assignTrainerTarget.name}
          onClose={() => setAssignTrainerTarget(null)}
          onAssigned={reload}
        />
      )}

      <Modal
        scrollBehavior="inside"
        isOpen={deleteMemberTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteMemberTarget(null)
            setDeleteError(null)
          }
        }}
      >
        <ModalContent>
          <ModalHeader>{t('members.deleteModalTitle')}</ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground/70">
              {t('members.deleteModalBody', { name: deleteMemberTarget?.name })}
            </p>
            {deleteError && <p className="text-sm text-danger">{deleteError}</p>}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              isDisabled={isDeleting}
              onClick={() => {
                setDeleteMemberTarget(null)
                setDeleteError(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button color="danger" isLoading={isDeleting} onClick={() => void handleDeleteMember()}>
              {t('members.deleteConfirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

/** A small grey tag after a name: guest, added at the desk, no account, child. */
function SmallTag({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-default/15 px-1.5 py-px text-[11px] font-normal text-foreground/55">
      {children}
    </span>
  )
}

function MoreIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="6" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="18" r="1.5" />
    </svg>
  )
}
